import { useState, useEffect, useCallback } from 'react';
import { CustomTemplate, TemplatesResponse } from '../types/messageForm.types';
import { apiService } from '../../../services/api';

export const useCustomTemplates = () => {
  console.log('[useCustomTemplates] Hook initialized');
  if (typeof window !== 'undefined') {
    console.warn('⚠️ useCustomTemplates HOOK CALLED - Check this appears in console!');
  }
  
  const [templates, setTemplates] = useState<CustomTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasTemplates, setHasTemplates] = useState(false);
  const [defaultTemplate, setDefaultTemplate] = useState<CustomTemplate | null>(null);

  const fetchTemplates = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Sending flows may only use templates Meta has approved.
      const response = await apiService.getWhatsAppCustomTemplates({ approvedOnly: true });

      if (response && response.data) {
        const templates = response.data.templates || [];
        
        // Debug: Log templates to see what we're receiving
        console.log('[useCustomTemplates] Fetched templates:', templates.length);
        console.log('[useCustomTemplates] RAW API Response:', JSON.stringify(response.data, null, 2));
        templates.forEach((template, index) => {
          console.log(`[useCustomTemplates] Template ${index + 1}:`, {
            id: template._id,
            name: template.templateName,
            hasParameters: template.hasParameters,
            parameters: template.parameters,
            parametersCount: template.parameters?.length || 0,
          });
        });
        
        setTemplates(templates);
        setHasTemplates(templates.length > 0);
        // Find default template (isDefault flag) or fall back to first active template
        const def = templates.find(t => t.isDefault && t.isActive) ?? templates.find(t => t.isActive) ?? null;
        setDefaultTemplate(def);
      } else {
        setTemplates([]);
        setHasTemplates(false);
        setDefaultTemplate(null);
      }
    } catch (err: any) {
      const errorMessage = err?.message || 'Failed to fetch templates';
      console.error('[useCustomTemplates] Error fetching custom templates:', err);
      setError(errorMessage);
      setTemplates([]);
      setHasTemplates(false);
      setDefaultTemplate(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    console.log('[useCustomTemplates] useEffect triggered, calling fetchTemplates');
    fetchTemplates();
  }, [fetchTemplates]);

  const getTemplateById = useCallback((templateId: string): CustomTemplate | undefined => {
    return templates.find(t => t._id === templateId);
  }, [templates]);

  const getTemplateByName = useCallback((templateName: string): CustomTemplate | undefined => {
    return templates.find(t => t.templateName === templateName);
  }, [templates]);

  return {
    templates,
    defaultTemplate,
    isLoading,
    error,
    hasTemplates,
    fetchTemplates,
    getTemplateById,
    getTemplateByName,
  };
};
